import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HelperStateService } from '../../../services/helperState.service';

export interface VwTourStep {
  // Matches a `[data-tour="key"]` attribute somewhere in the real layout —
  // steps whose target isn't present/visible are skipped automatically.
  key: string;
  title: string;
  body: string;
}

export const DASHBOARD_TOUR_KEY = 'tour.dashboard';

export const DASHBOARD_TOUR_STEPS: VwTourStep[] = [
  { key: 'brand', title: 'Welcome to VOWEL', body: "This is your visitor management dashboard. Let's take a quick look around." },
  { key: 'menu', title: 'Menu', body: 'Use this button to expand or collapse the sidebar, especially handy on smaller screens.' },
  { key: 'dash-tiles', title: 'Your numbers at a glance', body: 'These cards show live counts — registered users, current guests, staff, vehicles and more, based on what your role can see.' },
  { key: 'trend-chart', title: 'Visitor trends', body: 'Charts like this track visitor and vehicle activity so you can spot patterns over time.' },
  { key: 'property-filter', title: 'Filter by property', body: 'Pick a date range and a property here to scope a report before generating its graph.' },
  { key: 'theme-toggle', title: 'Light or dark', body: 'Switch between light and dark mode any time — your choice is remembered on this device.' },
  { key: 'panic-alert', title: 'Panic alerts', body: 'Urgent panic notifications show up here with a live count, so you never miss one.' },
  { key: 'help', title: 'Need help?', body: "Come back here any time to replay this tour, bring back tips you've hidden, or browse FAQs." },
];

@Injectable({ providedIn: 'root' })
export class TourService {
  private stepsSubject = new BehaviorSubject<VwTourStep[] | null>(null);
  private indexSubject = new BehaviorSubject<number>(0);

  steps$ = this.stepsSubject.asObservable();
  index$ = this.indexSubject.asObservable();

  constructor(private helperState: HelperStateService) {}

  get isActive(): boolean {
    return !!this.stepsSubject.value;
  }

  get steps(): VwTourStep[] {
    return this.stepsSubject.value || [];
  }

  get index(): number {
    return this.indexSubject.value;
  }

  // Filters to steps whose anchor actually exists and is visible right now
  // (role-gated elements come and go), so the tour never spotlights nothing.
  start(steps: VwTourStep[] = DASHBOARD_TOUR_STEPS, tourKey: string = DASHBOARD_TOUR_KEY): void {
    const visible = steps.filter(s => !!this.resolveTarget(s.key));
    if (!visible.length) {
      return;
    }
    this.activeTourKey = tourKey;
    this.stepsSubject.next(visible);
    this.indexSubject.next(0);
  }

  private activeTourKey: string = DASHBOARD_TOUR_KEY;

  next(): void {
    if (this.index >= this.steps.length - 1) {
      this.finish();
      return;
    }
    this.indexSubject.next(this.index + 1);
  }

  back(): void {
    if (this.index > 0) {
      this.indexSubject.next(this.index - 1);
    }
  }

  skip(): void {
    this.finish();
  }

  private finish(): void {
    this.helperState.markSeen(this.activeTourKey, true).subscribe();
    this.stepsSubject.next(null);
    this.indexSubject.next(0);
  }

  resolveTarget(key: string): HTMLElement | null {
    const el = document.querySelector(`[data-tour="${key}"]`) as HTMLElement;
    if (!el) {
      return null;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return null;
    }
    return el;
  }
}
